import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, TrendingUp, MessageSquare, Send, Briefcase } from 'lucide-react';

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreLabel = (score) => {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Great';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  if (score >= 20) return 'Poor';
  return 'Very Poor';
};

const getScoreBarColor = (score) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

export const RatingStatsPanel = ({ stats }) => {
  const hasRatings = (stats?.total_ratings || 0) > 0;
  const displayScore = hasRatings ? (stats?.average_score || 0) : 100;
  const displayLabel = hasRatings ? getScoreLabel(displayScore) : 'Excellent';

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-slate-50 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2"><Star className="h-5 w-5 text-yellow-500" /><span className="text-sm text-slate-600">Overall Score</span></div>
        <div className={`text-3xl font-bold ${getScoreColor(displayScore)}`}>{displayScore}<span className="text-base text-slate-400">/100</span></div>
        <p className={`text-sm font-medium ${getScoreColor(displayScore)}`}>{displayLabel}</p>
      </div>
      <div className="bg-slate-50 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2"><MessageSquare className="h-5 w-5 text-blue-500" /><span className="text-sm text-slate-600">Total Reviews</span></div>
        <div className="text-3xl font-bold text-slate-900">{stats?.total_ratings || 0}</div>
      </div>
      <div className="bg-green-50 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-5 w-5 text-green-600" /><span className="text-sm text-slate-600">Positive</span></div>
        <div className="text-3xl font-bold text-green-600">{stats?.recommend_count || 0}</div>
      </div>
      <div className="bg-blue-50 rounded-md p-4">
        <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-5 w-5 text-blue-600" /><span className="text-sm text-slate-600">Approval Rate</span></div>
        <div className="text-3xl font-bold text-blue-600">{hasRatings ? (stats?.recommendation_percentage || 0) : 100}%</div>
      </div>
    </div>
  );
};

export const RatingForm = ({ formData, setFormData, onSubmit, submitting, onCancel, targetName }) => (
  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
    className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
    <h3 className="font-heading text-xl font-bold text-slate-900 mb-4">Rate {targetName}</h3>
    <form onSubmit={onSubmit} className="space-y-4" data-testid="rating-form">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Your Score: <span className={`text-lg font-bold ${getScoreColor(formData.score)}`}>{formData.score}/100</span>
          <span className={`ml-2 text-sm ${getScoreColor(formData.score)}`}>({getScoreLabel(formData.score)})</span>
        </label>
        <input type="range" min="0" max="100" value={formData.score}
          onChange={(e) => setFormData({...formData, score: parseInt(e.target.value)})}
          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" data-testid="score-slider" />
        <div className="flex justify-between text-xs text-slate-400 mt-1"><span>0 - Poor</span><span>50 - Average</span><span>100 - Excellent</span></div>
      </div>
      <div>
        <label htmlFor="review_text" className="block text-sm font-medium text-slate-700 mb-2">Your Review (minimum 10 characters)</label>
        <textarea id="review_text" value={formData.review_text} onChange={(e) => setFormData({...formData, review_text: e.target.value})}
          rows={4} required className="w-full px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Share your experience..." data-testid="review-text-input" />
        <p className="text-xs text-slate-500 mt-1">{formData.review_text.length} characters</p>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-6 py-2 border border-slate-200 rounded-md font-semibold text-slate-700 hover:bg-slate-50" data-testid="cancel-review-btn">Cancel</button>
        <button type="submit" disabled={submitting} className="bg-blue-500 text-white px-6 py-2 rounded-md font-semibold hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2" data-testid="submit-review-btn">
          {submitting ? 'Submitting...' : 'Submit Review'} <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  </motion.div>
);

export const RatingCard = ({ rating, index, targetName, responding, setResponding, responseText, setResponseText, onRespond }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
    className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid={`rating-card-${index}`}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getScoreBarColor(rating.score)} text-white font-bold text-sm`}>{rating.score}</div>
        <div>
          <p className="font-semibold text-slate-900">{rating.reviewer_name}</p>
          <p className="text-sm text-slate-600">{new Date(rating.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>
      <div className="text-right">
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${rating.score >= 50 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{getScoreLabel(rating.score)}</span>
        {rating.job_title && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1 justify-end"><Briefcase className="h-3 w-3" />{rating.job_title}</p>}
      </div>
    </div>
    <div className="mb-3">
      <div className="w-full bg-slate-100 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${getScoreBarColor(rating.score)}`} style={{ width: `${rating.score}%` }} /></div>
    </div>
    <p className="text-slate-700 mb-4">{rating.review_text}</p>
    {rating.response && (
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mt-4">
        <div className="flex items-start gap-2 mb-2">
          <MessageSquare className="h-4 w-4 text-blue-600 mt-1" />
          <div className="flex-1"><p className="font-semibold text-blue-900">Response from {targetName}</p><p className="text-sm text-blue-700">{rating.response_at && new Date(rating.response_at).toLocaleDateString()}</p></div>
        </div>
        <p className="text-slate-700">{rating.response}</p>
      </div>
    )}
    {!rating.response && responding !== rating.id && (
      <button onClick={() => setResponding(rating.id)} className="text-blue-500 hover:text-blue-600 text-sm font-medium flex items-center gap-1" data-testid={`respond-btn-${index}`}>
        <MessageSquare className="h-4 w-4" />Respond
      </button>
    )}
    {responding === rating.id && (
      <div className="mt-4 space-y-3">
        <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={3}
          className="w-full px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500" placeholder="Write your response..." data-testid="response-text-input" />
        <div className="flex justify-end gap-2">
          <button onClick={() => { setResponding(null); setResponseText(''); }} className="px-4 py-2 text-slate-600 hover:text-slate-900" data-testid="cancel-response-btn">Cancel</button>
          <button onClick={() => onRespond(rating.id)} className="bg-blue-500 text-white px-4 py-2 rounded-md font-semibold hover:bg-blue-600" data-testid="submit-response-btn">Post Response</button>
        </div>
      </div>
    )}
  </motion.div>
);
